import { memo, useState, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { listTypeUnits, createTypeUnit, updateTypeUnitStatus, isTypeUnitError } from "@/infrastructure/type-unit/typeUnitService";
import type { TypeUnit, TypeUnitErrorCode, TypeUnitStatus } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { Pagination, usePagination } from "@/design-system";

export const TypeUnitPage = memo(function TypeUnitPage() {
  const { t } = useI18n();
  const { canCreateTypeUnit, canEditTypeUnit } = useRole();
  const [items, setItems] = useState<TypeUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<"all" | TypeUnitStatus>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try { setItems(await listTypeUnits()); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const getErrorMessage = useCallback((code: TypeUnitErrorCode): string => {
    const map: Record<TypeUnitErrorCode, string> = {
      DUPLICATE_CODE: t.typeUnit.errorDuplicateCode,
      DUPLICATE_ABBR: t.typeUnit.errorDuplicateAbbr,
      VALIDATION: t.typeUnit.errorValidation,
      NOT_FOUND: t.typeUnit.errorForbidden,
      FORBIDDEN: t.typeUnit.errorForbidden,
      UNAUTHORIZED: t.typeUnit.errorForbidden,
      UNKNOWN: t.typeUnit.errorForbidden,
    };
    return map[code];
  }, [t.typeUnit]);

  const handleCreate = useCallback(async (data: { code: string; name: string; abbreviation: string }) => {
    try {
      await createTypeUnit(data);
      showToast(t.typeUnit.successCreated, "success");
      setShowForm(false);
      fetchItems();
    } catch (err) {
      if (isTypeUnitError(err)) showToast(getErrorMessage(err.message as TypeUnitErrorCode), "error");
    }
  }, [fetchItems, showToast, t.typeUnit.successCreated, getErrorMessage]);

  const handleToggleStatus = useCallback(async (item: TypeUnit) => {
    const newStatus: TypeUnitStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateTypeUnitStatus(item.id, newStatus);
      showToast(t.typeUnit.successStatusChanged, "success");
      fetchItems();
    } catch (err) {
      if (isTypeUnitError(err)) showToast(getErrorMessage(err.message as TypeUnitErrorCode), "error");
    }
  }, [fetchItems, showToast, t.typeUnit.successStatusChanged, getErrorMessage]);

  const filtered = useMemo(() => items.filter(u => {
    const matchesFilter = filter === "all" || u.status === filter;
    const matchesSearch = search === "" || u.code.toLowerCase().includes(search.toLowerCase()) || u.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  }), [items, filter, search]);

  const { paginatedItems, totalPages, currentPage, totalItems } = usePagination(filtered, page);

  useEffect(() => { setPage(1); }, [filter, search]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.typeUnit.title}</h1>
          <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.typeUnit.subtitle}</p>
        </div>
        {canCreateTypeUnit && <button
          onClick={() => setShowForm(true)}
          style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }}
          onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)}
          onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          {t.typeUnit.addTypeUnit}
        </button>}
      </div>

      <Filters search={search} onSearch={setSearch} filter={filter} onFilter={setFilter} />

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && filtered.length === 0 && <EmptyState />}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label={t.typeUnit.title}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.typeUnit.colCode, t.typeUnit.colName, t.typeUnit.colAbbreviation, t.typeUnit.colStatus, t.typeUnit.colActions].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 20px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map(item => (
                  <UnitRow key={item.id} item={item} onToggleStatus={() => handleToggleStatus(item)} />
                ))}
              </tbody>
            </table>
            <div style={{ padding: "0 20px 16px" }}>
              <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setPage} />
            </div>
          </div>
        )}
      </div>

      {showForm && <CreateModal onSave={handleCreate} onClose={() => setShowForm(false)} />}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
});

const UnitRow = memo(function UnitRow({ item, onToggleStatus }: { item: TypeUnit; onToggleStatus: () => void }) {
  const { t } = useI18n();
  const { canEditTypeUnit } = useRole();
  const isActive = item.status === "ACTIVE";
  return (
    <tr style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }}
      onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")}
      onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}
    >
      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.code}</span></td>
      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.name}</span></td>
      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#888" }}>{item.abbreviation}</span></td>
      <td style={cell}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: isActive ? "rgba(34,197,94,0.1)" : "rgba(156,163,175,0.15)", color: isActive ? "#16a34a" : "#6b7280", fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}>
          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: isActive ? "#16a34a" : "#6b7280" }} aria-hidden="true" />
          {isActive ? t.typeUnit.statusActive : t.typeUnit.statusInactive}
        </span>
      </td>
      <td style={cell}>
        {canEditTypeUnit && <button
          onClick={onToggleStatus}
          style={{ fontFamily: typography.fontFamily, fontSize: "12px", fontWeight: typography.weight.semibold, color: isActive ? colors.error : "#16a34a", background: "transparent", border: `1px solid ${isActive ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)"}`, borderRadius: "7px", padding: "5px 12px", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}
        >
          {isActive ? t.typeUnit.deactivate : t.typeUnit.activate}
        </button>}
      </td>
    </tr>
  );
});

const cell: React.CSSProperties = { padding: "14px 20px", verticalAlign: "middle" };

const FILTER_KEYS = ["all", "ACTIVE", "INACTIVE"] as const;

const Filters = memo(function Filters({ search, onSearch, filter, onFilter }: { search: string; onSearch: (v: string) => void; filter: "all" | TypeUnitStatus; onFilter: (v: "all" | TypeUnitStatus) => void }) {
  const { t } = useI18n();
  const labels: Record<string, string> = { all: t.typeUnit.filterAll, ACTIVE: t.typeUnit.filterActive, INACTIVE: t.typeUnit.filterInactive };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
      <input type="search" value={search} onChange={e => onSearch(e.target.value)} placeholder={t.typeUnit.searchPlaceholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", minWidth: "200px", boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: "4px" }}>
        {FILTER_KEYS.map(key => (
          <button key={key} onClick={() => onFilter(key)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: filter === key ? typography.weight.semibold : typography.weight.medium, padding: "8px 16px", borderRadius: "8px", border: `1px solid ${filter === key ? "#111" : "#e8e8e8"}`, background: filter === key ? "#111" : "#fff", color: filter === key ? "#fff" : "#666", cursor: "pointer", transition: `all ${transitions.fast}`, whiteSpace: "nowrap" }}>{labels[key]}</button>
        ))}
      </div>
    </div>
  );
});

function CreateModal({ onSave, onClose }: { onSave: (data: { code: string; name: string; abbreviation: string }) => void; onClose: () => void }) {
  const { t } = useI18n();

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length < 2 || name.trim().length < 3 || abbreviation.trim().length < 1) {
      setLocalError(t.typeUnit.errorValidation);
      return;
    }
    setLocalError("");
    onSave({ code: code.trim().toUpperCase(), name: name.trim(), abbreviation: abbreviation.trim().toLowerCase() });
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.typeUnit.addTypeUnit}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Field label={t.typeUnit.codeLabel} value={code} onChange={setCode} placeholder={t.typeUnit.codePlaceholder} />
          <Field label={t.typeUnit.nameLabel} value={name} onChange={setName} placeholder={t.typeUnit.namePlaceholder} />
          <Field label={t.typeUnit.abbreviationLabel} value={abbreviation} onChange={setAbbreviation} placeholder={t.typeUnit.abbreviationPlaceholder} />
          {localError && <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: colors.error }}>{localError}</span>}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "4px" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.typeUnit.cancel}</button>
            <button type="submit" style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: "#111", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: "pointer", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = "#333")} onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = "#111")}>{t.typeUnit.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "12px 16px", outline: "none", transition: `border-color ${transitions.fast}`, width: "100%", boxSizing: "border-box" }} onFocus={e => (e.currentTarget.style.borderColor = "#111")} onBlur={e => (e.currentTarget.style.borderColor = "#e5e5e5")} />
    </div>
  );
}

function Toast({ message, type }: { message: string; type: "success" | "error" }) {
  return <div role="alert" style={{ position: "fixed", bottom: "24px", right: "24px", background: type === "success" ? "#111" : colors.error, color: "#fff", fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, padding: "12px 20px", borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 300, animation: "fadeInUp 0.3s ease" }}>{message}</div>;
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 24px", gap: "12px", textAlign: "center" }}>
      <div style={{ width: "48px", height: "48px", background: "#f5f5f5", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ccc" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
      </div>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.semibold, color: "#444", margin: 0 }}>{t.typeUnit.emptyTitle}</p>
      <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb", margin: 0, maxWidth: "300px" }}>{t.typeUnit.emptySubtitle}</p>
    </div>
  );
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}
